import {ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, SimpleChanges} from "@angular/core";
import {AssetValuationScenario} from "../../../../models/asset-valuation/asset-valuation-scenario";
import {AssetValuationHistoryBase} from "../../../../models/asset-valuation/asset-valuation-history-base";
import {AssetValuationDetailActions} from "../../../../store/asset.actions";
import {Store} from "@ngrx/store";
import {CodeTableEnum} from "../../../../../shared/model/code";
import {selectIsAnyAssetFormInEditMode, selectRelatedFundCurrencyIsoCode} from "../../../../store/asset/asset.selectors";
import {isOfScenarioType, ScenarioType} from "../../../../../shared/model/scenario-type";
import {selectFinancialPerformanceRecordsByScenarioType, selectSelectedExitDateAssumption} from "../../../../store/asset-valuation/asset-valuation.selectors";
import {map, Observable, of} from "rxjs";
import {FinancialPerformanceRecord} from "../../../../models/asset/financial-performance-record";
import {EMPTY_CODE, TraceableCode} from "../../../../../shared/model/traceable";
import {isCashflowTypeEnum} from "../../../../models/asset-valuation/cashflow-type.enum";
import {FormArray, FormBuilder} from "@angular/forms";
import {AssetCashflow} from "../../../../models/asset-valuation/asset-cashflow";
import {DecimalFormatPipe} from "../../../../../shared/pipes/decimal-format/decimal-format.pipe";

@Component({
    selector: "valumize-asset-financial-operational-valuation-scenario",
    templateUrl: "./asset-financial-operational-valuation-scenario.component.html",
    styleUrls: ["./asset-financial-operational-valuation-scenario.component.scss"],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetFinancialOperationalValuationScenarioComponent implements OnInit, OnChanges {

    @Input() assetId?: number;
    @Input() assetValuationId?: number;
    @Input() scenario?: AssetValuationScenario;
    @Input() cashflowsAreEditable = false;
    @Input() recordsAreEditable = false;
    @Input() earningBase: TraceableCode = EMPTY_CODE;
    @Input() historyBase?: AssetValuationHistoryBase;

    quickForecastOpen = false;
    showWalkToProceeds = false;
    forecastRecords$: Observable<FinancialPerformanceRecord[]> = of([]);

    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);

    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    selectedExitDateAssumptionOperational$ = this.store.select(selectSelectedExitDateAssumption);
    fundCurrencyIso$ = this.store.select(selectRelatedFundCurrencyIsoCode);

    cashflowsToPersist: AssetCashflow[] = [];
    codeTableScenarios = CodeTableEnum.SHARED_SCENARIOTYPE;
    codeTableEarningBase = CodeTableEnum.ASSET_EARNING_BASE;
    codeTableCashflowType = CodeTableEnum.SHARED_CASHFLOWTYPE;
    cashflowTableColumns = ["name", "amount", "delete"];

    constructor() {
    }

    cashflowForm = this.formBuilder.group({
        cashflows: this.formBuilder.array([])
    })

    get cashflows(): FormArray {
        return this.cashflowForm.get("cashflows") as FormArray;
    }

    ngOnInit() {
        this.scenario?.cashflows.forEach(cashflow => {
            this.cashflowsToPersist.push(cashflow);
            this.cashflows.push(this.formBuilder.group(cashflow))
        });
        const scenarioCode = this.scenario?.scenario.code;
        if (scenarioCode && isOfScenarioType(scenarioCode)) {
            this.forecastRecords$ = this.store
                .select(selectFinancialPerformanceRecordsByScenarioType(scenarioCode as ScenarioType))
                .pipe(map(records => records.filter(r => r.forecast)));
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["scenario"] && this.scenario?.cashflows) {
            this.cashflows.clear();
            this.cashflowsToPersist = [...this.scenario.cashflows];
            this.scenario.cashflows.forEach(cashflow => {
                this.cashflows.push(this.formBuilder.group(cashflow));
            });
        }
        this.recordsAreEditable = changes["recordsAreEditable"]?.currentValue ?? this.recordsAreEditable;
    }

    editCashflows = (scenarioType?: string) => {
        if (scenarioType && isOfScenarioType(scenarioType)) {
            this.store.dispatch(AssetValuationDetailActions.editCashflows({scenarioType}));
        }
    };

    cancelCashflows = (scenarioType?: string) => {
        if (scenarioType && isOfScenarioType(scenarioType)) {
            this.store.dispatch(AssetValuationDetailActions.cancelCashflows({scenarioType}));
        }
    };

    toggleQuickForecast = () => {
        this.quickForecastOpen = !this.quickForecastOpen;
    };

    toggleWalkToProceeds = () => {
        this.showWalkToProceeds = !this.showWalkToProceeds;
    };

    closeQuickForecast = () => {
        this.quickForecastOpen = false;
    };

    saveCashflows(): void {
        const scenarioType = this.scenario?.scenario.code;
        if (this.assetValuationId && this.scenario && scenarioType && isOfScenarioType(scenarioType)) {
            const updatedScenario = {
                ...this.scenario,
                cashflows: this.cashflowsToPersist
            };
            this.store.dispatch(AssetValuationDetailActions.saveCashflows({assetValuationId: this.assetValuationId, scenario: updatedScenario, scenarioType}));
        }
    }

    deleteCashflows(element: any) {
        const scenarioType = this.scenario?.scenario.code;
        if (this.assetValuationId && this.scenario && scenarioType && isOfScenarioType(scenarioType)) {
            const updatedScenario = {
                ...this.scenario,
                cashflows: this.scenario.cashflows.filter(cashflow => cashflow.id !== element.id)
            };
            this.store.dispatch(AssetValuationDetailActions.saveCashflows({assetValuationId: this.assetValuationId, scenario: updatedScenario, scenarioType}));
        }
    }

    onFormValueChange(event: Event, element: any, index: number): void {
        const input = event.target as HTMLInputElement;
        const parsedValue = parseFloat(input.value);
        const cashflowToUpdate = this.cashflowForm.value.cashflows?.[index] as AssetCashflow;

        if (this.cashflowForm.valid && this.scenario && !isNaN(parsedValue)) {
            this.cashflowsToPersist = this.cashflowsToPersist.map((cashflow: AssetCashflow) => {
                if (cashflowToUpdate.id === cashflow.id) {
                    return {
                        ...cashflow,
                        amount: {
                            ...cashflow.amount,
                            amount: DecimalFormatPipe.transformToMillionsNum(parsedValue)
                        }
                    };
                }
                return cashflow;
            });
        }
    }

    isEditableCashflow = (element: AssetCashflow): boolean => !!element.cashflowType.code && isCashflowTypeEnum(element.cashflowType.code);

    editableCashflowsExist = (cashflows: AssetCashflow[]): boolean => cashflows.some(cashflow => this.isEditableCashflow(cashflow));
}
